import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Show } from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { ArtworkPlaceholder } from '@/components/ArtworkPlaceholder';
import { Play } from '@/components/icons';

const CARD_W = 'w-[42vw] shrink-0 sm:w-[180px] md:w-[164px] lg:w-[176px]';

/** Portrait poster card; shows a branded placeholder artwork until art exists. */
export function PosterCard({ show, action }: { show: Show; action?: ReactNode }) {
  return (
    <div className={cn('group relative', CARD_W)}>
      <Link to={`/shows/${show.slug}`} className="block">
        <div className="relative aspect-[2/3] overflow-hidden rounded-card border border-border transition-transform duration-200 group-hover:scale-[1.04]">
          {show.posterUrl ? (
            <img
              src={show.posterUrl}
              alt={show.title}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : (
            <ArtworkPlaceholder seed={show.id} title={show.title} />
          )}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-3">
            <p className="font-display text-sm font-bold leading-tight text-white line-clamp-2">
              {show.title}
            </p>
          </div>
          <div className="absolute inset-0 grid place-items-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-brand-gradient text-white shadow-glow">
              <Play size={20} />
            </span>
          </div>
        </div>
      </Link>
      {action && <div className="absolute right-2 top-2">{action}</div>}
    </div>
  );
}

/** Horizontally-scrolling row of poster cards. */
export function PosterRow({
  title,
  shows,
  renderAction,
}: {
  title: string;
  shows: Show[];
  renderAction?: (show: Show) => ReactNode;
}) {
  if (shows.length === 0) return null;
  return (
    <section>
      <h2 className="mb-3 font-display text-lg font-bold text-ink">{title}</h2>
      <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2 [-ms-overflow-style:none] [scrollbar-width:none]">
        {shows.map((s) => (
          <PosterCard key={s.id} show={s} action={renderAction?.(s)} />
        ))}
      </div>
    </section>
  );
}
